import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { styled } from '@linaria/react';
import { OfferViewContext, OfferViewType } from 'context/offerViewContext';
import { getOverlayVisibilityState } from 'modules/overlay/selectors';
import { hideOverlay } from 'modules/overlay/slice';
import { OverlayType } from 'modules/overlay/types';
import { getOfferEntity, getSearchParameters, getSplitBookingDetails, getSplitBookingPriceSummary } from 'modules/sapiSearch/selectors';
import { getTopOffers } from 'modules/sapiSearch/utils';
import { getIsOTAMode } from 'modules/search/selectors';
import { getNumberOfNights } from 'utils/offers';
import { BottomSheet } from '@daedalus/atlas/BottomSheet';
import { useDeviceLayout } from '@daedalus/atlas/context/deviceLayout';
import { Divider } from '@daedalus/atlas/Divider';
import { Flex } from '@daedalus/atlas/helpers/Flex';
import { Icon } from '@daedalus/atlas/Icon';
import { PopUp } from '@daedalus/atlas/PopUp';
import { Text } from '@daedalus/atlas/Text';
import { roomsToConfigurationObject } from '@daedalus/core/src/room/business/roomConfiguration';
import { useFormattedDate } from '../helpers';
import { messages } from '../messages';
import { SplitBookingDetailsOfferItem } from './SplitBookingDetailsOfferItem';
import { SplitBookingDetailsPriceBreakdown } from './SplitBookingDetailsPriceBreakdown';
const Wrapper = /*#__PURE__*/styled('div')({
  name: "Wrapper",
  class: "w15firsv",
  propsAsIs: false
});
const DatesWrapper = /*#__PURE__*/styled('div')({
  name: "DatesWrapper",
  class: "d1ssk2vz",
  propsAsIs: false
});
const _exp3 = /*#__PURE__*/() => Flex.Row;
const DateHeadingWrapper = /*#__PURE__*/styled(_exp3())({
  name: "DateHeadingWrapper",
  class: "dt6zep5",
  propsAsIs: true
});
const OffersWrapper = /*#__PURE__*/styled('div')({
  name: "OffersWrapper",
  class: "os2e8cx",
  propsAsIs: false
});

/**
 * Split booking bottom sheet on mobile and popup on desktop
 */
interface Props {
  isAccommodationPage?: boolean;
}
export const SplitBookingDetails: React.FC<Props> = ({
  isAccommodationPage = false
}) => {
  const {
    formatMessage
  } = useIntl();
  const {
    formatDate
  } = useFormattedDate();
  const dispatch = useDispatch();
  const isOpen = useSelector(state => getOverlayVisibilityState(state, OverlayType.SplitBookingDetails));
  const {
    checkIn,
    checkOut,
    rooms,
    hotelId
  } = useSelector(getSearchParameters);
  const offerEntity = useSelector(state => getOfferEntity(state, hotelId));
  const isOTAMode = useSelector(getIsOTAMode);
  const topOffers = getTopOffers(isOTAMode, offerEntity);
  const {
    numberOfAdults,
    numberOfChildren,
    numberOfRooms
  } = roomsToConfigurationObject(rooms);
  const numberOfNights = getNumberOfNights(checkIn, checkOut);
  const numberOfGuests = numberOfAdults + numberOfChildren;
  const {
    splitBookingOffers,
    splitBookingType
  } = useSelector(state => getSplitBookingDetails(state, hotelId));
  const {
    isMobile
  } = useDeviceLayout();
  const sbPriceSummary = useSelector(state => getSplitBookingPriceSummary(state, hotelId));
  const onClose = useCallback(() => {
    dispatch(hideOverlay());
  }, [dispatch]);
  const content = <Wrapper data-id="SplitBookingDetails">
      <DatesWrapper>
        <DateHeadingWrapper alignItems="center" gap="s300">
          <Icon name="Calendar" />
          <Text variant="titleM">{formatMessage(messages.dates)}</Text>
        </DateHeadingWrapper>
        <Text>
          {formatDate(checkIn)} - {formatDate(checkOut)}
        </Text>
      </DatesWrapper>

      <Text>{formatMessage(messages.bottomSheetTitle)}</Text>

      <OffersWrapper>
        <OfferViewContext.Provider value={OfferViewType.SPLIT_FLOW_OVERLAY}>
          {splitBookingOffers?.map((offer, index) => <SplitBookingDetailsOfferItem key={offer.offer.id} offerIdx={index} splitBookingOffer={offer} splitBookingType={splitBookingType} splitBookingPosition={topOffers?.length ?? 0} isAccommodationPage={isAccommodationPage} isSeparatorVisible={index < splitBookingOffers.length - 1} />)}
        </OfferViewContext.Provider>
      </OffersWrapper>

      <Divider />

      <SplitBookingDetailsPriceBreakdown numberOfGuests={numberOfGuests} numberOfNights={numberOfNights} numberOfRooms={numberOfRooms} splitBookingRateInfo={sbPriceSummary} />
    </Wrapper>;
  if (isMobile) {
    return <BottomSheet isOpen={isOpen} titleBarProps={{
      centerContent: formatMessage(messages.header)
    }} onClose={onClose} disableDrag={true}>
        {content}
      </BottomSheet>;
  }
  return <PopUp isOpen={isOpen} titleBarProps={{
    centerContent: <Text variant="titleM">{formatMessage(messages.header)}</Text>
  }} closeAction={onClose} width={544}>
      {content}
    </PopUp>;
};

require("./SplitBookingDetails.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./SplitBookingDetails.tsx");